import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { CommunicationManagerService } from '../services/communication-manager.service';
import { LocalStorageService } from './storage.service';
import { User } from '../models/user.model';

export const StubUser: User = {
  _id: '',
  firstName: '',
  lastName: '',
  email: '',
  accessToken: '',
  initials: '',
  lastLogin: new Date(),
  isAdmin: false,
};

@Injectable({
  providedIn: 'root',
})
export class SessionManagerService implements OnDestroy {
  private currentUser: User | null = null;
  private user$: BehaviorSubject<any> = new BehaviorSubject(null);
  private userRole$: BehaviorSubject<string> = new BehaviorSubject('guest');
  private userRole = 'guest';

  private currentCompany: string | undefined;

  constructor(
    private cm: CommunicationManagerService,
    private router: Router,
    // private route: ActivatedRouteSnapshot,
    private localStorageService: LocalStorageService
  ) {
    this.currentUser =
      JSON.parse(sessionStorage.getItem('user') || '{}') ?? undefined;
    this.attemptResumeSession();
  }

  ngOnDestroy(): void {
    if (this.reloadTimeout) {
      clearTimeout(this.reloadTimeout);
    }
  }

  getUserRole() {
    return this.userRole;
  }

  attemptResumeSession() {
    if (
      document.location.pathname.startsWith('/user/verify') ||
      document.location.pathname.startsWith('/forgot-password') ||
      document.location.pathname.startsWith('/user/resetPassword')
    ) {
      return;
    } else if (this.currentUser && this.currentUser.accessToken) {
      this.cm.setUserToken(this.currentUser.accessToken || '');
      this.cm.get('user', []).subscribe({
        next: (success: User) => {
          this.setCurrentUser(success);
          if (!success.companies?.length) {
            // const status = this.route.queryParamMap.get('status');

            // if (status === 'success') {
            //   this.router.navigate(['/welcome']);
            // }

            // if (status === 'failed') {
            //   this.router.navigate(['/stripe']);
            // }
            // //console.log("NO COMPANIES FOR USER");
            // this.router.navigate(['/welcome']);

            this.router.navigate(['/subscription']);
            // this.router.navigate(['/settings/subscription'])
          } else if (
            document.location.pathname == '' ||
            document.location.pathname == '/' ||
            document.location.pathname == '/login'
          ) {
            // TODO check before redirect for company/org data
            // if new user without company/org create dummy and redirect
            this.router.navigate(['/dashboard']);
          }
        },
        error: () => {
          // //console.log('bad token');
          // this.logout();
          // this.router.navigate(['/login']);
        },
      });
      return;
    } else if (
      !document.location.pathname.startsWith('/login') &&
      !document.location.pathname.startsWith('/welcome') &&
      !document.location.pathname.startsWith('/subscription') &&
      !document.location.pathname.startsWith('/forgot-password') &&
      !document.location.pathname.startsWith('/user/resetPassword')
    ) {
      this.logout();
    }
  }

  setCurrentUser(user: User | null) {
    if (!user) {
      this.currentUser = null;
      sessionStorage.removeItem('user');
      this.cm.setUserToken('');
      this.userRole = 'guest';
      this.userRole$.next('guest');
      this.user$.next(null);
      this.currentCompany = undefined;
      return;
    }

    this.currentUser = user;
    this.currentUser.initials =
      user.firstName?.[0]?.toUpperCase() + user.lastName?.[0]?.toUpperCase();

    sessionStorage.setItem('user', JSON.stringify(this.currentUser));
    this.cm.setUserToken(this.currentUser.accessToken || '');
    this.user$.next(this.currentUser);

    // Note: Session timeout will be reset by the SessionTimeoutService when it detects user activity

    const lsCompany = this.localStorageService.getCurrentCompany();

    if (!this.currentCompany && lsCompany?._id) {
      // this.localStorageService.clearData
      this.setCurrentCompany(lsCompany._id);
    } else if (this.currentCompany) {
      const currentCompRole = this.currentUser?.companies?.find(
        (cItem) => cItem.company === this.currentCompany
      );

      if (currentCompRole && currentCompRole?.role) {
        this.userRole$.next(currentCompRole.role);
        this.userRole = currentCompRole.role;
      } else {
        this.userRole$.next('guest');
        this.userRole = 'guest';
      }
    } else if (this.currentUser?.companies?.length) {
      this.setCurrentCompany(this.currentUser.companies[0]?.company);
      // //console.log('no company');
    }
  }

  getUser(): User | null {
    return this.currentUser;
  }

  getUserObservable() {
    return this.user$.asObservable();
  }

  getUserRoleObservable() {
    return this.userRole$.asObservable();
  }

  private reloadTimeout: any;

  reloadOnLogoutForFullReset() {
    if (this.reloadTimeout) {
      clearTimeout(this.reloadTimeout);
    }

    this.reloadTimeout = setTimeout(() => {
      window.location.reload();
      clearTimeout(this.reloadTimeout);
    }, 10);
  }

  logout() {
    // Clear all storage including IndexedDB, localStorage, sessionStorage, and cache
    this.localStorageService.clearData();
    this.setCurrentUser(null);
    this.currentCompany = undefined;
    
    // Force navigation to login page
    this.router.navigate(['/login']).then(() => {
      this.reloadOnLogoutForFullReset();
    }).catch((error) => {
      //console.error('Navigation to login failed:', error);
      // Fallback: force page reload to login
      window.location.href = '/login';
    });

    // this.reloadOnLogoutForFullReset();
  }

  login(username: string, password: string): Observable<User> {
    return this.cm.postAndGetJson(
      'user/login',
      {
        usernameOrEmail: username,
        password: password,
      },
      []
    );
  }

  setCurrentCompany(id: string) {
    this.currentCompany = id;
    const currentCompRole = this.currentUser?.companies?.find(
      (cItem) => cItem.company === id
    );

    // //console.log('currentCompRole =============> ', currentCompRole);

    if (currentCompRole && currentCompRole?.role) {
      this.userRole$.next(currentCompRole.role);
      this.userRole = currentCompRole.role;
    } else {
      this.userRole$.next('guest');
      this.userRole = 'guest';
    }
  }

  getCurrentCompany() {
    return this.currentCompany;
  }

  /**
   * Note: Session timeout methods have been moved to SessionTimeoutService
   * to avoid circular dependency. Use SessionTimeoutService directly for timeout operations.
   */
}
